import { httpClient } from '@/lib/httpClient';

export interface MerchandisingCollection {
  id: number;
  customerId: number;
  name: string;
  description: string;
  apiPath: string;
  slicesCount: number;
  createdAt: Date;
  updatedAt: Date;
  opaqueId: string | null;
}

export interface CreateMerchandisingCollectionDto {
  name: string;
  description: string;
}

export interface UpdateMerchandisingCollectionDto {
  name: string;
  description: string;
}

export interface QueryFilterDto {
  page: number;
  limit: number;
}

export interface MerchandisingCollectionsQueryFilterDto {
  name?: string;
  orderBy?: 'createdAt' | 'updatedAt';
  orderDirection?: 'ASC' | 'DESC';
  page: number;
  limit: number;
}

export interface MerchandisingCollectionsResponse {
  currentPage: number;
  totalItems: number;
  totalPages: number;
  items: MerchandisingCollection[];
}

export enum SliceInputType {
  DEFAULT = 'DEFAULT',
  AUDIENCE = 'AUDIENCE',
  AI = 'AI',
  CSV = 'CSV',
}

export interface SliceFilters {
  brokenStock: {
    active: boolean;
  };
  discount: {
    type: 'ALL' | 'WITH_DISCOUNT' | 'WITHOUT_DISCOUNT';
    range: {
      from: number;
      to: number;
    };
    active: boolean;
  };
  price: {
    range: {
      from: number;
      to: number;
    };
    active: boolean;
  };
  lowStock: {
    stockLimit: number;
    active: boolean;
  };
  releasePeriod: {
    startsAt: Date;
    endsAt: Date;
    active: boolean;
  };
}

export interface SliceAdditionalInfo {
  filters?: {
    minPrice: string;
    maxPrice: string;
  };
}

export interface MerchandisingCollectionSlice {
  id: number;
  collectionId: number;
  name: string;
  inputType: SliceInputType | null;
  inputValue: string | null;
  filters: SliceFilters | null;
  additionalInfo: SliceAdditionalInfo | null;
  productsCount: number;
  startsAt: Date;
  endsAt: Date;
  createdAt: Date;
  updatedAt: Date;
}

export interface CreateMerchandisingCollectionSliceDto {
  name: string;
  inputType: SliceInputType | null;
  inputValue: string | null;
  filters?: Record<string, any>;
  sortOrder?: Record<string, any>;
  startsAt: Date;
  endsAt: Date;
}

export interface MerchandisingCollectionSlicesQueryFilterDto {
  name?: string;
  orderBy?: 'createdAt' | 'updatedAt' | 'startsAt' | 'endsAt';
  orderDirection?: 'ASC' | 'DESC';
  page: number;
  limit: number;
}

export interface MerchandisingCollectionSlicesResponse {
  currentPage: number;
  totalItems: number;
  totalPages: number;
  items: MerchandisingCollectionSlice[];
}

export interface UpdateMerchandisingCollectionSliceDto {
  name: string;
  inputType?: SliceInputType | null;
  inputValue?: string | null;
  filters?: Record<string, any> | null;
  additionalInfo?: Record<string, any> | null;
  startsAt: Date;
  endsAt: Date;
}

export interface MerchandisingCollectionSliceProduct {
  id: number;
  sliceId: number;
  productId: string;
  position: number;
  active: boolean;
  name: string;
  visionCategoryName: string;
  price: string;
  salePrice: string;
  stock: boolean;
  image1: string;
}

export interface MerchandisingCollectionSliceProductResponse {
  currentPage: number;
  totalItems: number;
  totalPages: number;
  items: MerchandisingCollectionSliceProduct[];
}

export interface CreateMerchandisingCollectionSliceProductDto {
  productId: string;
  addToFirstPosition?: boolean;
}

export interface CreateMerchandisingCollectionSliceProductResponse {
  success: boolean;
  message: string;
  data: {
    slice: MerchandisingCollectionSlice;
    sliceProduct: MerchandisingCollectionSliceProduct;
  };
}

export interface ReorderMerchandisingCollectionSliceProductDto {
  products: string[];
}

export interface ReorderMerchandisingCollectionSliceProductResponseDto {
  positions: number[];
}

export interface RemoveMerchandisingCollectionSliceProductResponse {
  success: boolean;
  message: string;
  data: {
    slice: MerchandisingCollectionSlice;
    sliceProduct: MerchandisingCollectionSliceProduct;
  };
}

export interface MerchandisingCollectionPublicLinkResponse {
  opaqueId: string | null;
}

class MerchandisingCollectionsService {
  constructor() {}

  async createCollection(body: CreateMerchandisingCollectionDto) {
    const { data } = await httpClient.post<MerchandisingCollection>(
      '/v1/merchandising-collections',
      body,
    );

    return data;
  }

  async findCollections(
    queryFilterDto: MerchandisingCollectionsQueryFilterDto,
  ) {
    const { data } = await httpClient.get<MerchandisingCollectionsResponse>(
      '/v1/merchandising-collections',
      {
        params: queryFilterDto,
      },
    );

    return data;
  }

  async findCollection(id: number) {
    const { data } = await httpClient.get<MerchandisingCollection>(
      `/v1/merchandising-collections/${id}`,
    );

    return data;
  }

  async updateCollection(id: number, dto: UpdateMerchandisingCollectionDto) {
    const { data } = await httpClient.patch<MerchandisingCollection>(
      `/v1/merchandising-collections/${id}`,
      dto,
    );

    return data;
  }

  async removeCollection(id: number) {
    const { data } = await httpClient.delete<number>(
      `/v1/merchandising-collections/${id}`,
    );

    return data;
  }

  async createSlice(
    collectionId: number,
    body: CreateMerchandisingCollectionSliceDto,
  ) {
    const { data } = await httpClient.post<MerchandisingCollectionSlice>(
      `/v1/merchandising-collections/${collectionId}/slices`,
      body,
    );

    return data;
  }

  async findSlices(
    collectionId: number,
    queryFilterDto: MerchandisingCollectionSlicesQueryFilterDto,
  ) {
    const { data } =
      await httpClient.get<MerchandisingCollectionSlicesResponse>(
        `/v1/merchandising-collections/${collectionId}/slices`,
        {
          params: queryFilterDto,
        },
      );

    return data;
  }

  async findSlice(collectionId: number, sliceId: number) {
    const { data } = await httpClient.get<MerchandisingCollectionSlice>(
      `/v1/merchandising-collections/${collectionId}/slices/${sliceId}`,
    );

    return data;
  }

  async updateSlice(
    collectionId: number,
    sliceId: number,
    dto: UpdateMerchandisingCollectionSliceDto,
  ) {
    const { data } = await httpClient.patch<MerchandisingCollectionSlice>(
      `/v1/merchandising-collections/${collectionId}/slices/${sliceId}`,
      dto,
    );

    return data;
  }

  async removeSlice(collectionId: number, sliceId: number) {
    const { data } = await httpClient.delete<number>(
      `/v1/merchandising-collections/${collectionId}/slices/${sliceId}`,
    );

    return data;
  }

  async createSliceProduct(
    collectionId: number,
    sliceId: number,
    dto: CreateMerchandisingCollectionSliceProductDto,
  ) {
    const { data } =
      await httpClient.post<CreateMerchandisingCollectionSliceProductResponse>(
        `/v1/merchandising-collections/${collectionId}/slices/${sliceId}/products`,
        dto,
      );

    return data;
  }

  async findSliceProducts(
    collectionId: number,
    sliceId: number,
    queryFilter: QueryFilterDto,
  ) {
    const { data } =
      await httpClient.get<MerchandisingCollectionSliceProductResponse>(
        `/v1/merchandising-collections/${collectionId}/slices/${sliceId}/products`,
        {
          params: queryFilter,
        },
      );

    return data;
  }

  async reorderSliceProduct(
    collectionId: number,
    sliceId: number,
    dto: ReorderMerchandisingCollectionSliceProductDto,
  ) {
    const { data } =
      await httpClient.post<ReorderMerchandisingCollectionSliceProductResponseDto>(
        `/v1/merchandising-collections/${collectionId}/slices/${sliceId}/products/reorder`,
        dto,
      );

    return data;
  }

  async removeSliceProduct(
    collectionId: number,
    sliceId: number,
    productId: string,
  ) {
    const { data } =
      await httpClient.delete<RemoveMerchandisingCollectionSliceProductResponse>(
        `/v1/merchandising-collections/${collectionId}/slices/${sliceId}/products/${productId}`,
      );

    return data;
  }

  async generatePublicLink(collectionId: number) {
    const { data } =
      await httpClient.post<MerchandisingCollectionPublicLinkResponse>(
        `/v1/merchandising-collections/public-link/${collectionId}`,
      );

    return data;
  }
}

export default new MerchandisingCollectionsService();
